import { faArrowAltCircleDown, faArrowAltCircleUp, faEdit, faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import { faDownload, faPlane, faPlus, faSignal, faSyncAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable, { CaptionButton } from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FormDataControl } from '../../components/FormFields'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import LicenseService from '../../service/LicenseService'
import { CLusterSlotUnavailable, NodeLicenseUsed, TenantLicenseUsed } from '../../utils/GeneralUtil'
import GatewayType from '../GatewayType'

const License: GatewayType = () => {
  const { t } = useTranslation()
  const [license, setLicense] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { isLicensed, setLicensed, setLicenseStatus, isClusterSlotReserved, setClusterSlotReserved } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    LicenseService.getLicense()
      .then((license) => {
        setLicense(license)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (license.status !== undefined) {
      setLicenseStatus(license.status)
      setLicensed(license.status === 'LEGACY' || license.status === 'TENANT_LICENSE' || license.status === 'ACQUIRED')
      setClusterSlotReserved(license.clusterSlotReserved)
    }
  }, [license, setLicenseStatus, setLicensed, setClusterSlotReserved])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
      },
    ]
  }, [])

  const data = getData(license, t)

  var licenseButtons: CaptionButton[] = []

  getLicenseButtons(license, licenseButtons, t, editDialogPropsSetter, setLicense, confirmDialogPropsSetter)

  return (
    <Loading isLoading={loading}>
      {license.status === 'LEGACY' ? (
        <NodeLicenseUsed onClick={() => deleteLicense(t, confirmDialogPropsSetter, setLicense)} permissions={Authorization.settings.license.edit} />
      ) : license.status === 'TENANT_LICENSE' ? (
        <TenantLicenseUsed />
      ) : license.status === 'ACQUIRED' && !isClusterSlotReserved ? (
        <CLusterSlotUnavailable />
      ) : (
        <>
          {!isLicensed && license.status !== undefined && (
            <>
              <h1>{t('gatewayNotLicensed')}</h1>
              <hr />
            </>
          )}
          <DataTable
            keyField='label'
            data={data}
            columns={columns}
            headerClasses='d-none'
            searchable={false}
            caption={t('license.text')}
            helpLink={t('license.helpLink')}
          />
          <div className='text-right pr-1 ml-nfc-1'>
            {licenseButtons.map((button, index) => {
              const { onClick, variant = 'primary', text, permissions, icon = faPlus } = button
              return (
                <ProtectedButton variant={variant} className='ml-auto my-auto' onClick={onClick} permissions={permissions} key={`License-Button-${index}`}>
                  <FontAwesomeIcon icon={icon} /> {text !== undefined && <span className='ml-1 my-auto'>{text}</span>}
                </ProtectedButton>
              )
            })}
          </div>
        </>
      )}
    </Loading>
  )
}

function getLicenseButtons(
  license: any,
  licenseButtons: CaptionButton[],
  t: TFunction,
  editDialogPropsSetter: (editDialogProps: EditDialogProps) => void,
  setLicense: React.Dispatch<any>,
  confirmDialogPropsSetter: (confirmDialogProps: ConfirmDialogProps) => void
) {
  if (license.status === 'BLANK' || license.status === 'ACQUIRABLE' || license.status === 'NO_CONTINGENT_AVAILABLE') {
    licenseButtons.push({
      onClick: () => updateCustomerInfo(t, editDialogPropsSetter, setLicense, license),
      text: t('customerInfo.edit'),
      icon: faEdit,
      permissions: Authorization.settings.license.edit,
    })
  }
  if (license.status === 'ACQUIRABLE' || license.status === 'NO_CONTINGENT_AVAILABLE') {
    licenseButtons.push({
      onClick: () => deleteCustomerInfo(t, confirmDialogPropsSetter, setLicense),
      text: t('customerInfo.delete'),
      variant: 'danger',
      icon: faTrashAlt,
      permissions: Authorization.settings.license.edit,
    })
  }
  if (license.offlineModePermitted === true) {
    licenseButtons.push({
      onClick: () => goOffline(t, confirmDialogPropsSetter, setLicense),
      text: t('license.offlineMode'),
      icon: faPlane,
      permissions: Authorization.settings.license.edit,
    })
  }
  if (license.mode === 'OFFLINE') {
    licenseButtons.push({
      onClick: () => goOnline(t, confirmDialogPropsSetter, setLicense),
      text: t('license.onlineMode'),
      icon: faSignal,
      permissions: Authorization.settings.license.edit,
    })
    licenseButtons.push({
      onClick: () => processLicenseServerResponse(t, editDialogPropsSetter, setLicense),
      text: t('license.processLicenseServerResponseHeader'),
      icon: faSyncAlt,
      permissions: Authorization.settings.license.edit,
    })
  }

  if (license.status === 'ACQUIRABLE' && license.mode === 'ONLINE') {
    licenseButtons.push({
      onClick: () => acquireLicense(t, editDialogPropsSetter, setLicense, license),
      text: t('license.acquireLicenseHeader'),
      icon: faArrowAltCircleDown,
      permissions: Authorization.settings.license.edit,
    })
  }
  if (license.status === 'ACQUIRED' && license.mode === 'ONLINE') {
    licenseButtons.push({
      onClick: () => relinquishLicense(t, confirmDialogPropsSetter, setLicense),
      text: t('license.relinquishHeader'),
      icon: faArrowAltCircleUp,
      permissions: Authorization.settings.license.edit,
    })
  }

  if (license.status === 'ACQUIRABLE' && license.mode === 'OFFLINE') {
    licenseButtons.push({
      onClick: () => acquireLicenseRequest(t, editDialogPropsSetter),
      text: t('license.acquireRequestHeader'),
      icon: faDownload,
      permissions: Authorization.settings.license.edit,
    })
  }
  if (license.status === 'ACQUIRED' && license.mode === 'OFFLINE') {
    licenseButtons.push({
      onClick: () => verifyRequest(t, confirmDialogPropsSetter),
      text: t('license.verifyRequestHeader'),
      icon: faDownload,
      permissions: Authorization.settings.license.edit,
    })
    licenseButtons.push({
      onClick: () => relinquishRequest(t, confirmDialogPropsSetter),
      text: t('license.relinquishRequestHeader'),
      icon: faDownload,
      permissions: Authorization.settings.license.edit,
    })
  }
}

function getData(license: any, t: TFunction) {
  const customerInfo: Map<string, string> = new Map(license?.key?.split(/\s*,\s*/).map((chunk: any) => chunk.split('=')))

  var licenseStatus = t(`licenseStatus.${license.status}`)
  if (license.status === 'NO_CONTINGENT_AVAILABLE') {
    if (license.offlineModePermitted === true) {
      licenseStatus = t('licenseStatus.OFFLINE')
    }
  }
  return license.status !== 'LEGACY' && license.status !== 'ACQUIRED' && license.status !== 'TENANT_LICENSE'
    ? [
        {
          label: t('organisation.text'),
          value: customerInfo.get('O'),
        },
        {
          label: t('customerNumber.text'),
          value: license.customerNumber,
        },
        {
          label: t('commonName.text'),
          value: customerInfo.get('CN'),
        },
        {
          label: t('organizationalUnit.text'),
          value: customerInfo.get('OU'),
        },
        {
          label: t('licenseCode.text'),
          value: license.licenseCode,
        },
        {
          label: t('licenseStatus.text'),
          value: licenseStatus,
        },
      ]
    : license.status === 'ACQUIRED'
    ? [
        {
          label: t('productName'),
          value: license.licenseProductCodeDisplayName,
        },
        {
          label: t('customerNumber.text'),
          value: license.customerNumber,
        },
        {
          label: t('gatewayKeyInformation'),
          value: license.key,
        },
        {
          label: t('hardwareId'),
          value: license.installationHash,
        },
        {
          label: t('nextLicenseValidation'),
          value: new Date(license.nextValidationEnd).toString(),
        },
      ]
    : []
}

function updateCustomerInfo(t: TFunction, editDialogPropsSetter: any, setLicense: React.Dispatch<any>, license: any) {
  const customerInfo: Map<string, string> = new Map(license.key?.split(/\s*,\s*/).map((chunk: any) => chunk.split('=')))
  const props: EditDialogProps = {
    show: true,
    header: t('license.addCustomerInfoHeader'),
    labelGridSize: 3,
    onSubmit: LicenseService.updateCustomerInfo,
    onSubmitExtraArgs: [t, setLicense],
    data: [
      {
        id: 'organisation',
        value: customerInfo.get('O'),
        type: 'text',
        label: t('organisation.text'),
        placeholder: t('organisation.text'),
        autoComplete: 'organisation',
      },
      {
        id: 'customerNumber',
        value: license.customerNumber,
        type: 'text',
        label: t('customerNumber.text'),
        placeholder: t('customerNumber.text'),
        autoComplete: 'customerNumber',
      },
      {
        id: 'commonName',
        value: customerInfo.get('CN'),
        type: 'text',
        label: t('commonName.text'),
        placeholder: t('commonName.text'),
        autoComplete: 'commonName',
      },
      {
        id: 'organizationalUnit',
        value: customerInfo.get('OU'),
        type: 'text',
        label: t('organizationalUnit.text'),
        placeholder: t('organizationalUnit.text'),
        autoComplete: 'organizationalUnit',
      },
      {
        id: 'licenseCode',
        value: license.licenseCode,
        type: 'text',
        label: t('licenseCode.text'),
        placeholder: t('licenseCode.text'),
        autoComplete: 'licenseCode',
      },
    ],
  }

  editDialogPropsSetter(props)
}

function deleteCustomerInfo(t: TFunction, confirmDialogPropsSetter: any, setLicense: React.Dispatch<any>) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.deleteCustomerInfoHeader'),
    body: t('license.deleteCustomerInfoBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.deleteCustomerInfo,
    onSubmitExtraArgs: [t, setLicense],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

function processLicenseServerResponse(t: TFunction, editDialogPropsSetter: any, setLicense: React.Dispatch<any>) {
  const props: EditDialogProps = {
    show: true,
    header: t('license.processLicenseServerResponseHeader'),
    labelGridSize: 3,
    onSubmit: LicenseService.processLicenseServerResponse,
    onSubmitExtraArgs: [t, setLicense],
    data: [
      {
        id: 'responseFromServer',
        type: 'file',
        label: t('license.serverResponse'),
        required: true,
        requiredMessage: t('serverResponse.inValidMessage'),
      },
    ],
  }

  editDialogPropsSetter(props)
}

function acquireLicense(t: TFunction, editDialogPropsSetter: any, setLicense: React.Dispatch<any>, license: any) {
  var options: any[] = []
  if (license?.availableProductCodes.GATEWAY_CLUSTER.availableUnits > 0) {
    options.push({ label: 'Gateway Cluster', value: 'GATEWAY_CLUSTER' })
  }
  if (license?.availableProductCodes.MULTITENANCY.availableUnits > 0) {
    options.push({ label: 'Control Cluster', value: 'MULTITENANCY' })
  }
  const props: EditDialogProps = {
    show: true,
    header: t('license.acquireLicenseHeader'),
    labelGridSize: 3,
    onSubmit: LicenseService.acquireLicense,
    onSubmitExtraArgs: [t, setLicense],
    data: (custom: string[]) => {
      const maxActiveNodes = license?.availableProductCodes[custom[0]]?.restrictionContingents?.maxActiveNodes?.max
      return [
        {
          id: 'mode',
          type: 'select',
          options,
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
          value: custom[0],
          label: t('license.gatewayMode'),
          required: true,
          render: options.length > 1,
        },
        {
          id: 'maxActiveNodes',
          type: 'number',
          label: t('maxActiveNodes.text'),
          placeholder: t('maxActiveNodes.text'),
          required: true,
          requiredMessage: t('maxActiveNodes.requiredMessage'),
          min: '1',
          minInvalidMessage: t('minActiveNodes.invalidMessage'),
          max: maxActiveNodes,
          maxInvalidMessage: t('maxActiveNodes.invalidMessage', { maxActiveNodes }),
        },
        ...getAdditionalDataField(license, custom[0], t),
      ]
    },
    initCustom: [options.length > 0 ? options[0].value : ''],
  }

  editDialogPropsSetter(props)
}

function getAdditionalDataField(license: any, custom: string, t: TFunction): FormDataControl[] {
  if (custom === 'MULTITENANCY') {
    const maxTenantCluster = license.availableProductCodes.MULTITENANCY.restrictionContingents.maxTenantCluster.max
    return [
      {
        id: 'maxTenantCluster',
        type: 'number',
        label: t('maxTenantCluster.text'),
        placeholder: t('maxTenantCluster.text'),
        required: true,
        requiredMessage: t('maxTenantCluster.requiredMessage'),
        min: '1',
        minInvalidMessage: t('minTenantSize.invalidMessage'),
        max: maxTenantCluster,
        maxInvalidMessage: t('maxTenantCluster.invalidMessage', { maxTenantCluster }),
      },
    ]
  }
  return []
}

function relinquishLicense(t: TFunction, confirmDialogPropsSetter: any, setLicense: React.Dispatch<any>) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.relinquishHeader'),
    body: t('license.relinquishBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.relinquishLicense,
    onSubmitExtraArgs: [t, setLicense],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

function acquireLicenseRequest(t: TFunction, editDialogPropsSetter: any) {
  const props: EditDialogProps = {
    show: true,
    header: t('license.acquireRequestHeader'),
    labelGridSize: 3,
    onSubmit: LicenseService.acquireLicenseRequest,
    data: (custom: string[]) => [
      {
        id: 'mode',
        type: 'select',
        options: [
          { label: 'Gateway Cluster', value: 'GATEWAY_CLUSTER' },
          { label: 'Control Cluster', value: 'MULTITENANCY' },
        ],
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('license.gatewayMode'),
        required: true,
      },
      {
        id: 'maxActiveNodes',
        type: 'number',
        label: t('maxActiveNodes.text'),
        placeholder: t('maxActiveNodes.text'),
        required: true,
        requiredMessage: t('maxActiveNodes.requiredMessage'),
        min: '1',
        minInvalidMessage: t('minActiveNodes.invalidMessage'),
      },
      ...getAcquireReqField(custom[0], t),
    ],
    initCustom: ['GATEWAY_CLUSTER'],
  }

  editDialogPropsSetter(props)
}

function getAcquireReqField(custom: string, t: TFunction): FormDataControl[] {
  if (custom === 'MULTITENANCY') {
    return [
      {
        id: 'maxTenantCluster',
        type: 'number',
        label: t('maxTenantCluster.text'),
        placeholder: t('maxTenantCluster.text'),
        required: true,
        requiredMessage: t('maxTenantCluster.requiredMessage'),
        min: '1',
        minInvalidMessage: t('minTenantSize.invalidMessage'),
      },
    ]
  }
  return []
}

function verifyRequest(t: TFunction, confirmDialogPropsSetter: any) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.verifyRequestHeader'),
    body: t('license.verifyRequestBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.verifyRequest,
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

function relinquishRequest(t: TFunction, confirmDialogPropsSetter: any) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.relinquishRequestHeader'),
    body: t('license.relinquishRequestBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.relinquishLicenseRequest,
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

function deleteLicense(t: TFunction, confirmDialogPropsSetter: any, setLicense: React.Dispatch<any>) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.deleteHeader'),
    body: t('license.deleteBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.deleteLicense,
    onSubmitExtraArgs: [t, setLicense],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

function goOffline(t: TFunction, confirmDialogPropsSetter: any, setLicense: React.Dispatch<any>) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.offlineMode'),
    body: t('license.offlineModeBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.offlineMode,
    onSubmitExtraArgs: [t, setLicense],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

function goOnline(t: TFunction, confirmDialogPropsSetter: any, setLicense: React.Dispatch<any>) {
  const props: ConfirmDialogProps = {
    show: true,
    header: t('license.onlineMode'),
    body: t('license.onlineModeBody'),
    submitText: t('yes'),
    onSubmit: LicenseService.onlineMode,
    onSubmitExtraArgs: [t, setLicense],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(props)
}

export default License
